import { TableCell, TableCellProps, TableSortLabel } from '@mui/material';
import { ReactNode } from 'react';
import type { SortDirection } from './use-sortable-table';

interface SortableHeadCellProps extends Omit<TableCellProps, 'onClick'> {
  /** Whether this column is the active sort column. */
  active: boolean;
  /** Current sort direction (only meaningful while `active`). */
  direction: SortDirection;
  /** Invoked when the header is clicked to change the sort. */
  onSort: () => void;
  children: ReactNode;
}

/**
 * A table header cell with a clickable MUI sort indicator. Spread the result of
 * `useSortableTable().getSortProps(field)` to wire it to the sort state.
 */
export function SortableHeadCell({
  active,
  direction,
  onSort,
  children,
  ...cellProps
}: SortableHeadCellProps) {
  return (
    <TableCell {...cellProps} sortDirection={active ? direction : false}>
      <TableSortLabel
        active={active}
        direction={active ? direction : 'asc'}
        onClick={onSort}
        sx={{
          // Inherit the header cell's colour so the label and arrow stay legible
          // on both light and dark (e.g. past-due) table headers.
          color: 'inherit',
          '&.Mui-active': { color: 'inherit' },
          '&:hover': { color: 'inherit' },
          '&.Mui-active .MuiTableSortLabel-icon, & .MuiTableSortLabel-icon': {
            color: 'inherit',
          },
        }}
      >
        {children}
      </TableSortLabel>
    </TableCell>
  );
}
